import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, defer, from, of, Subscription } from 'rxjs';
import { finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

type AttachmentCacheEntry = {
  key: string;
  dataUrl: string;
  updatedAt: number;
  lastAccessedAt: number;
  size: number;
};

@Injectable({ providedIn: 'root' })
export class FileAttachmentService {
  private readonly sessionPrefix = 'cg:fileAttachment:dataUrl:';
  private readonly persistentPrefix = 'cg:fileAttachment:dataUrl:persistent:';
  private readonly scopeMarkerKey = 'cg:fileAttachment:companyScope';
  private readonly skipErrorToastHeader = 'x-cg-skip-error-toast';
  private readonly indexedDbName = 'cg-file-attachment-cache-db';
  private readonly indexedDbStoreName = 'attachmentDataUrls';
  private readonly maxCacheEntries = 500;
  private readonly maxCacheBytes = 80 * 1024 * 1024;
  private readonly maxDownloadBatchSize = 10;
  private readonly downloadBatchDelayMs = 600;
  private readonly memoryCache = new Map<string, string>();
  private readonly inFlightDataUrlRequests = new Map<
    string,
    Observable<string>
  >();
  private readonly pendingDownloadJobs: Array<{
    id: number;
    cancelled: boolean;
    started: boolean;
    start: () => void;
  }> = [];
  private nextDownloadJobId = 1;
  private activeDownloadCount = 0;
  private isDownloadBatchRunning = false;
  private downloadBatchTimer: ReturnType<typeof setTimeout> | null = null;
  private dbOpenPromise: Promise<IDBDatabase> | null = null;

  constructor(private http: HttpClient) {
    this.cleanupLegacyStorageCache();
  }

  downloadFileAttachment(
    fileAttachmentId: string,
    options?: {
      suppressErrorToast?: boolean;
      ignoreCompanyScopeCache?: boolean;
    },
  ): Observable<Blob> {
    const url = `${environment.apiUrl}/api/Emission/DownloadFileAttachment/${fileAttachmentId}`;
    const headersConfig: Record<string, string> = { Accept: '*/*' };
    if (options?.suppressErrorToast) {
      headersConfig[this.skipErrorToastHeader] = '1';
    }

    const headers = new HttpHeaders(headersConfig);
    if (options?.ignoreCompanyScopeCache) {
      // Company-select ekraninda coklu logo istegini 10'lu batch ile kisitliyoruz.
      return this.enqueueAttachmentDownload(url, headers);
    }

    return this.http.get(url, {
      responseType: 'blob',
      headers,
    });
  }

  getFileAttachmentDataUrl(
    fileAttachmentId: string,
    options?: {
      suppressErrorToast?: boolean;
      ignoreCompanyScopeCache?: boolean;
    },
  ): Observable<string> {
    const normalizedId = String(fileAttachmentId || '').trim();
    if (!normalizedId) {
      return of('');
    }

    const companyScope = this.resolveCurrentCompanyScope();
    this.ensureScopeConsistency(companyScope);
    const cacheKey = this.resolveCacheKey(
      normalizedId,
      companyScope,
      !!options?.ignoreCompanyScopeCache,
    );

    const inFlightRequest = this.inFlightDataUrlRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    return defer(() => from(this.readCache(cacheKey))).pipe(
      switchMap((cachedValue) => {
        if (cachedValue) {
          return of(cachedValue);
        }

        const runningRequest = this.inFlightDataUrlRequests.get(cacheKey);
        if (runningRequest) {
          return runningRequest;
        }

        const request$ = this.downloadFileAttachment(
          normalizedId,
          options,
        ).pipe(
          switchMap((blob) => this.blobToDataUrl(blob)),
          map((dataUrl) => {
            void this.writeCache(cacheKey, dataUrl);
            return dataUrl;
          }),
          finalize(() => this.inFlightDataUrlRequests.delete(cacheKey)),
          shareReplay(1),
        );

        this.inFlightDataUrlRequests.set(cacheKey, request$);
        return request$;
      }),
    );
  }

  clearFileAttachmentDataUrlCacheForCurrentCompany(): void {
    const companyScope = this.resolveCurrentCompanyScope();
    this.ensureScopeConsistency(companyScope);
    this.removeMemoryKeysByPrefix(`${this.sessionPrefix}${companyScope}:`);
    this.removeMemoryKeysByPrefix(`${this.sessionPrefix}global:`);
    void this.removeIndexedDbByPrefix(`${this.sessionPrefix}${companyScope}:`);
    void this.removeIndexedDbByPrefix(`${this.sessionPrefix}global:`);
    void this.removeIndexedDbByPrefix(
      `${this.persistentPrefix}${companyScope}:`,
    );
    void this.removeIndexedDbByPrefix(`${this.persistentPrefix}global:`);

    // Scoped keys
    this.removeKeysByPrefix(
      sessionStorage,
      `${this.sessionPrefix}${companyScope}:`,
    );
    this.removeKeysByPrefix(
      localStorage,
      `${this.persistentPrefix}${companyScope}:`,
    );
    // Global keys — logo guncellendiginde global cache de temizlenmeli,
    // aksi halde company-select'teki eski logo kalici kalir.
    this.removeKeysByPrefix(sessionStorage, `${this.sessionPrefix}global:`);
    this.removeKeysByPrefix(localStorage, `${this.persistentPrefix}global:`);
  }

  clearFileAttachmentDataUrlCacheByAttachmentId(
    fileAttachmentId: string | null | undefined,
  ): void {
    const normalizedId = String(fileAttachmentId || '').trim();
    if (!normalizedId) {
      return;
    }
    const companyScope = this.resolveCurrentCompanyScope();
    this.ensureScopeConsistency(companyScope);
    const scopedKey = this.buildCacheKey(normalizedId, companyScope);
    const globalKey = this.buildGlobalCacheKey(normalizedId);
    const scopedPersistentKey = this.buildPersistentCacheKey(
      normalizedId,
      companyScope,
    );
    const globalPersistentKey =
      this.buildPersistentGlobalCacheKey(normalizedId);

    this.memoryCache.delete(scopedKey);
    this.memoryCache.delete(globalKey);
    this.memoryCache.delete(scopedPersistentKey);
    this.memoryCache.delete(globalPersistentKey);

    void this.removeIndexedDbKey(scopedKey);
    void this.removeIndexedDbKey(globalKey);
    void this.removeIndexedDbKey(scopedPersistentKey);
    void this.removeIndexedDbKey(globalPersistentKey);

    sessionStorage.removeItem(scopedKey);
    sessionStorage.removeItem(globalKey);
    localStorage.removeItem(scopedPersistentKey);
    localStorage.removeItem(globalPersistentKey);
  }

  private resolveCurrentCompanyScope(): string {
    const selected =
      sessionStorage.getItem('applicationAdminSelectedCompanyId') ||
      sessionStorage.getItem('applicationAdminActiveCompanyId');
    if (selected) {
      return selected;
    }

    try {
      const rawUser = localStorage.getItem('currentUser');
      if (!rawUser) {
        return 'no-company';
      }
      const user = JSON.parse(rawUser);
      return String(user?.companyId || user?.branchId || 'no-company');
    } catch {
      return 'no-company';
    }
  }

  private ensureScopeConsistency(scope: string): void {
    const previousScope = sessionStorage.getItem(this.scopeMarkerKey);
    if (previousScope && previousScope !== scope) {
      this.clearAllAttachmentDataUrlCache();
    }
    sessionStorage.setItem(this.scopeMarkerKey, scope);
  }

  private clearAllAttachmentDataUrlCache(): void {
    this.removeMemoryKeysByPrefix(
      this.sessionPrefix,
      `${this.sessionPrefix}global:`,
    );
    this.removeMemoryKeysByPrefix(
      this.persistentPrefix,
      `${this.persistentPrefix}global:`,
    );
    void this.removeIndexedDbByPrefix(
      this.sessionPrefix,
      `${this.sessionPrefix}global:`,
    );
    void this.removeIndexedDbByPrefix(
      this.persistentPrefix,
      `${this.persistentPrefix}global:`,
    );

    this.removeScopedKeysByPrefix(
      sessionStorage,
      this.sessionPrefix,
      `${this.sessionPrefix}global:`,
    );
    this.removeScopedKeysByPrefix(
      localStorage,
      this.persistentPrefix,
      `${this.persistentPrefix}global:`,
    );
  }

  private buildCacheKey(
    fileAttachmentId: string,
    companyScope: string,
  ): string {
    return `${this.sessionPrefix}${companyScope}:${fileAttachmentId}`;
  }

  private buildGlobalCacheKey(fileAttachmentId: string): string {
    return `${this.sessionPrefix}global:${fileAttachmentId}`;
  }

  private buildPersistentCacheKey(
    fileAttachmentId: string,
    companyScope: string,
  ): string {
    return `${this.persistentPrefix}${companyScope}:${fileAttachmentId}`;
  }

  private buildPersistentGlobalCacheKey(fileAttachmentId: string): string {
    return `${this.persistentPrefix}global:${fileAttachmentId}`;
  }

  private resolveCacheKey(
    fileAttachmentId: string,
    companyScope: string,
    ignoreCompanyScopeCache: boolean,
  ): string {
    if (ignoreCompanyScopeCache) {
      return this.buildGlobalCacheKey(fileAttachmentId);
    }
    return this.buildCacheKey(fileAttachmentId, companyScope);
  }

  private async readCacheAsync(key: string): Promise<string | null> {
    const memoryValue = this.memoryCache.get(key);
    if (memoryValue) {
      return memoryValue;
    }

    const attachmentIdFromKey = this.extractAttachmentIdFromCacheKey(key);
    if (attachmentIdFromKey) {
      const memoryByAttachmentId =
        this.findMemoryCacheByAttachmentId(attachmentIdFromKey);
      if (memoryByAttachmentId) {
        this.memoryCache.set(key, memoryByAttachmentId);
        return memoryByAttachmentId;
      }
    }

    const fromIndexedDb = await this.readIndexedDbDataUrl(key);
    if (fromIndexedDb) {
      this.memoryCache.set(key, fromIndexedDb);
      return fromIndexedDb;
    }

    const persistentKey = key.replace(
      this.sessionPrefix,
      this.persistentPrefix,
    );
    const persistentFromIndexedDb =
      await this.readIndexedDbDataUrl(persistentKey);
    if (persistentFromIndexedDb) {
      this.memoryCache.set(key, persistentFromIndexedDb);
      return persistentFromIndexedDb;
    }

    if (attachmentIdFromKey) {
      const fallbackByAttachmentId =
        await this.readIndexedDbDataUrlByAttachmentId(attachmentIdFromKey);
      if (fallbackByAttachmentId) {
        this.memoryCache.set(key, fallbackByAttachmentId);
        void this.writeCache(key, fallbackByAttachmentId);
        return fallbackByAttachmentId;
      }
    }

    const legacyValue = this.readLegacyStorageCache(key);
    if (legacyValue) {
      this.memoryCache.set(key, legacyValue);
      void this.writeCache(key, legacyValue);
      this.removeLegacyStorageCacheKey(key);
      return legacyValue;
    }

    return null;
  }

  private readLegacyStorageCache(key: string): string | null {
    try {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue) {
        return sessionValue;
      }

      const persistentKey = key.replace(
        this.sessionPrefix,
        this.persistentPrefix,
      );
      const persistentValue = localStorage.getItem(persistentKey);
      if (persistentValue) {
        // Session cache lostsa local cache ile hizli warm-up yap.
        // sessionStorage kota/asim hatasi olursa yine de persistent cache degerini kullan.
        try {
          sessionStorage.setItem(key, persistentValue);
        } catch {
          // no-op
        }
        return persistentValue;
      }

      return null;
    } catch {
      return null;
    }
  }

  private async writeCache(key: string, value: string): Promise<void> {
    this.memoryCache.set(key, value);

    // Keep memory cache bounded as a lightweight LRU approximation.
    if (this.memoryCache.size > this.maxCacheEntries) {
      const oldestKey = this.memoryCache.keys().next().value;
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
      }
    }

    const persistentKey = key.replace(
      this.sessionPrefix,
      this.persistentPrefix,
    );
    await this.writeIndexedDbEntry(key, value);
    if (persistentKey !== key) {
      await this.writeIndexedDbEntry(persistentKey, value);
    }
    await this.pruneIndexedDbIfNeeded();
  }

  private removeLegacyStorageCacheKey(key: string): void {
    try {
      sessionStorage.removeItem(key);
      const persistentKey = key.replace(
        this.sessionPrefix,
        this.persistentPrefix,
      );
      localStorage.removeItem(persistentKey);
    } catch {
      // no-op
    }
  }

  private removeMemoryKeysByPrefix(
    prefix: string,
    preservedPrefix?: string,
  ): void {
    const keysToDelete: string[] = [];
    for (const key of this.memoryCache.keys()) {
      if (!key.startsWith(prefix)) {
        continue;
      }
      if (preservedPrefix && key.startsWith(preservedPrefix)) {
        continue;
      }
      keysToDelete.push(key);
    }
    keysToDelete.forEach((key) => this.memoryCache.delete(key));
  }

  private removeKeysByPrefix(storage: Storage, prefix: string): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  private removeScopedKeysByPrefix(
    storage: Storage,
    prefix: string,
    preservedPrefix: string,
  ): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix) && !key.startsWith(preservedPrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  private cleanupLegacyStorageCache(): void {
    // Existing deployments may already have large data URLs in Web Storage.
    // Clear only known attachment-cache keys to free quota without touching other app keys.
    this.removeKeysByPrefix(sessionStorage, this.sessionPrefix);
    this.removeKeysByPrefix(localStorage, this.persistentPrefix);
  }

  private async openIndexedDb(): Promise<IDBDatabase> {
    if (typeof indexedDB === 'undefined') {
      throw new Error('indexedDB not available');
    }

    if (!this.dbOpenPromise) {
      this.dbOpenPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(this.indexedDbName, 1);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(this.indexedDbStoreName)) {
            db.createObjectStore(this.indexedDbStoreName, { keyPath: 'key' });
          }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }).catch((error) => {
        this.dbOpenPromise = null;
        throw error;
      });
    }

    return this.dbOpenPromise;
  }

  private async readIndexedDbDataUrl(key: string): Promise<string | null> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      const store = tx.objectStore(this.indexedDbStoreName);
      const req = store.get(key);

      const entry = (await this.requestAsPromise<
        AttachmentCacheEntry | undefined
      >(req)) as AttachmentCacheEntry | undefined;

      if (!entry?.dataUrl) {
        return null;
      }

      entry.lastAccessedAt = Date.now();
      store.put(entry);
      await this.transactionDone(tx);
      return entry.dataUrl;
    } catch {
      return null;
    }
  }

  private async readIndexedDbDataUrlByAttachmentId(
    attachmentId: string,
  ): Promise<string | null> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      const store = tx.objectStore(this.indexedDbStoreName);
      const bestEntry = await new Promise<AttachmentCacheEntry | null>(
        (resolve) => {
          let matchedEntry: AttachmentCacheEntry | null = null;
          const req = store.openCursor();
          req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor) {
              resolve(matchedEntry);
              return;
            }

            const entry = cursor.value as AttachmentCacheEntry;
            const key = String(entry?.key || '');
            const isMatch = key.endsWith(`:${attachmentId}`);

            if (isMatch && entry?.dataUrl) {
              if (
                !matchedEntry ||
                (entry.lastAccessedAt || entry.updatedAt) >
                  (matchedEntry.lastAccessedAt || matchedEntry.updatedAt)
              ) {
                matchedEntry = entry;
              }
            }

            cursor.continue();
          };
          req.onerror = () => resolve(null);
        },
      );

      if (!bestEntry?.dataUrl) {
        await this.transactionDone(tx);
        return null;
      }

      bestEntry.lastAccessedAt = Date.now();
      store.put(bestEntry);
      await this.transactionDone(tx);
      return bestEntry.dataUrl;
    } catch {
      return null;
    }
  }

  private findMemoryCacheByAttachmentId(attachmentId: string): string | null {
    for (const [memoryKey, value] of this.memoryCache.entries()) {
      if (memoryKey.endsWith(`:${attachmentId}`) && value) {
        return value;
      }
    }
    return null;
  }

  private extractAttachmentIdFromCacheKey(key: string): string | null {
    const normalized = String(key || '').trim();
    if (!normalized) {
      return null;
    }
    const idx = normalized.lastIndexOf(':');
    if (idx < 0 || idx >= normalized.length - 1) {
      return null;
    }
    const maybeId = normalized.slice(idx + 1).trim();
    return this.isGuid(maybeId) ? maybeId : null;
  }

  private isGuid(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(value.trim());
  }

  private async writeIndexedDbEntry(
    key: string,
    dataUrl: string,
  ): Promise<void> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      const store = tx.objectStore(this.indexedDbStoreName);
      const now = Date.now();
      const entry: AttachmentCacheEntry = {
        key,
        dataUrl,
        updatedAt: now,
        lastAccessedAt: now,
        size: this.estimateDataUrlBytes(dataUrl),
      };
      store.put(entry);
      await this.transactionDone(tx);
    } catch {
      // no-op
    }
  }

  private async removeIndexedDbKey(key: string): Promise<void> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      tx.objectStore(this.indexedDbStoreName).delete(key);
      await this.transactionDone(tx);
    } catch {
      // no-op
    }
  }

  private async removeIndexedDbByPrefix(
    prefix: string,
    preservedPrefix?: string,
  ): Promise<void> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      const store = tx.objectStore(this.indexedDbStoreName);

      await new Promise<void>((resolve) => {
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            resolve();
            return;
          }

          const currentKey = String(cursor.key || '');
          const shouldDelete =
            currentKey.startsWith(prefix) &&
            (!preservedPrefix || !currentKey.startsWith(preservedPrefix));

          if (shouldDelete) {
            cursor.delete();
          }
          cursor.continue();
        };
        cursorReq.onerror = () => resolve();
      });

      await this.transactionDone(tx);
    } catch {
      // no-op
    }
  }

  private async pruneIndexedDbIfNeeded(): Promise<void> {
    try {
      const db = await this.openIndexedDb();
      const tx = db.transaction(this.indexedDbStoreName, 'readwrite');
      const store = tx.objectStore(this.indexedDbStoreName);

      const entries = await new Promise<AttachmentCacheEntry[]>((resolve) => {
        const out: AttachmentCacheEntry[] = [];
        const req = store.openCursor();

        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) {
            resolve(out);
            return;
          }
          const value = cursor.value as AttachmentCacheEntry;
          if (value?.key) {
            out.push(value);
          }
          cursor.continue();
        };

        req.onerror = () => resolve(out);
      });

      let totalBytes = entries.reduce((acc, item) => acc + (item.size || 0), 0);
      let remainingEntries = entries.length;
      if (
        remainingEntries <= this.maxCacheEntries &&
        totalBytes <= this.maxCacheBytes
      ) {
        await this.transactionDone(tx);
        return;
      }

      const sorted = [...entries].sort(
        (a, b) =>
          (a.lastAccessedAt || a.updatedAt) - (b.lastAccessedAt || b.updatedAt),
      );

      for (const entry of sorted) {
        if (
          remainingEntries <= this.maxCacheEntries &&
          totalBytes <= this.maxCacheBytes
        ) {
          break;
        }
        store.delete(entry.key);
        totalBytes -= entry.size || 0;
        remainingEntries -= 1;
      }

      await this.transactionDone(tx);
    } catch {
      // no-op
    }
  }

  private estimateDataUrlBytes(dataUrl: string): number {
    return dataUrl.length * 2;
  }

  private requestAsPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private transactionDone(tx: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(tx.error);
      tx.onerror = () => reject(tx.error);
    });
  }

  private blobToDataUrl(blob: Blob): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(String(reader.result || ''));
        observer.complete();
      };
      reader.onerror = () => {
        observer.error(reader.error);
      };
      reader.readAsDataURL(blob);
    });
  }

  private enqueueAttachmentDownload(
    url: string,
    headers: HttpHeaders,
  ): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      let requestSubscription: Subscription | null = null;
      const jobId = this.nextDownloadJobId++;
      const job = {
        id: jobId,
        cancelled: false,
        started: false,
        start: () => {
          if (job.cancelled) {
            return;
          }

          job.started = true;
          this.activeDownloadCount += 1;
          requestSubscription = this.http
            .get(url, {
              responseType: 'blob',
              headers,
            })
            .pipe(
              finalize(() => {
                this.activeDownloadCount = Math.max(
                  0,
                  this.activeDownloadCount - 1,
                );
                this.handleDownloadCompletion();
              }),
            )
            .subscribe({
              next: (blob) => observer.next(blob),
              error: (error) => observer.error(error),
              complete: () => observer.complete(),
            });
        },
      };

      this.pendingDownloadJobs.push(job);
      this.tryStartNextDownloadBatch();

      return () => {
        job.cancelled = true;
        if (job.started) {
          requestSubscription?.unsubscribe();
          return;
        }
        this.removePendingDownloadJob(jobId);
      };
    });
  }

  private tryStartNextDownloadBatch(): void {
    if (this.isDownloadBatchRunning) {
      return;
    }

    this.clearBatchTimer();
    this.isDownloadBatchRunning = true;

    let started = 0;
    while (started < this.maxDownloadBatchSize) {
      const nextJob = this.shiftNextPendingDownloadJob();
      if (!nextJob) {
        break;
      }
      nextJob.start();
      started += 1;
    }

    if (started === 0) {
      this.isDownloadBatchRunning = false;
      return;
    }

    if (this.activeDownloadCount === 0) {
      this.isDownloadBatchRunning = false;
      this.scheduleNextDownloadBatch();
    }
  }

  private handleDownloadCompletion(): void {
    if (this.activeDownloadCount > 0) {
      return;
    }

    this.isDownloadBatchRunning = false;
    this.scheduleNextDownloadBatch();
  }

  private scheduleNextDownloadBatch(): void {
    if (this.downloadBatchTimer) {
      return;
    }

    if (!this.hasPendingDownloadJob()) {
      return;
    }

    // Batchler arasina kisa bir bekleme koyarak backend burst yukunu azaltir.
    this.downloadBatchTimer = setTimeout(() => {
      this.downloadBatchTimer = null;
      this.tryStartNextDownloadBatch();
    }, this.downloadBatchDelayMs);
  }

  private shiftNextPendingDownloadJob(): {
    id: number;
    cancelled: boolean;
    started: boolean;
    start: () => void;
  } | null {
    while (this.pendingDownloadJobs.length > 0) {
      const nextJob = this.pendingDownloadJobs.shift()!;
      if (!nextJob.cancelled) {
        return nextJob;
      }
    }

    return null;
  }

  private hasPendingDownloadJob(): boolean {
    return this.pendingDownloadJobs.some((job) => !job.cancelled);
  }

  private removePendingDownloadJob(jobId: number): void {
    const index = this.pendingDownloadJobs.findIndex((job) => job.id === jobId);
    if (index < 0) {
      return;
    }

    this.pendingDownloadJobs.splice(index, 1);
    if (!this.hasPendingDownloadJob()) {
      this.clearBatchTimer();
    }
  }

  private clearBatchTimer(): void {
    if (!this.downloadBatchTimer) {
      return;
    }
    clearTimeout(this.downloadBatchTimer);
    this.downloadBatchTimer = null;
  }

  private readCache(key: string): Promise<string | null> {
    return this.readCacheAsync(key);
  }
}
